import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";

export interface SwipePanel {
  id: string;
  label: string;
  render: () => ReactNode;
  /** Whether a pill in the tablist carries `id="swipe-tab-<id>"` to label this
   *  panel. The Feed has no pill, so it labels itself instead of pointing
   *  `aria-labelledby` at an element that doesn't exist. */
  hasTab?: boolean;
}

interface SwipeTabsProps {
  panels: SwipePanel[];
  index: number;
  onIndexChange: (index: number) => void;
  /** Explicit panel height. Flex stretches every panel to the tallest one, so
   *  letting content set this makes every tab as tall as the longest tab. */
  panelHeight?: string;
  /** Space reserved at the top of each panel for chrome that overlays it, and
   *  at the bottom to clear the fixed bottom nav. */
  panelPaddingTop?: number;
  panelPaddingBottom?: number;
  /** Scroll offset of the active panel, so overlaying chrome can slide away
   *  with the content the way a single document scroll would. */
  onActiveScroll?: (scrollTop: number) => void;
}

/** Walks up from the touch target looking for something that scrolls
 *  horizontally on its own — the product shelves in the feed, the filter row.
 *  A swipe that starts on one of those belongs to the shelf, not the pager. */
export function startsOnHorizontalScroller(target: EventTarget | null, root: HTMLElement): boolean {
  // Element, not HTMLElement: the icons in these rows are <svg>, which is an
  // SVGElement, so an HTMLElement check made the walk never start and handed the
  // drag to the pager. Touching a category icon — the biggest target in the
  // strip — swiped to the next tab while the gap beside it scrolled normally.
  let node: Element | null = target instanceof Element ? target : null;
  while (node && node !== root) {
    if (node.scrollWidth > node.clientWidth + 1) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
    node = node.parentElement;
  }
  return false;
}

export function SwipeTabs({
  panels,
  index,
  onIndexChange,
  panelHeight,
  panelPaddingTop,
  panelPaddingBottom,
  onActiveScroll,
}: SwipeTabsProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    duration: 20,
    skipSnaps: false,
    // The panel count never changes, so the MutationObserver behind this would
    // only fire reInit storms as list items stream in — each one remeasuring
    // every panel.
    watchSlides: false,
    watchResize: true,
    watchDrag: (_api, evt) => {
      const root = viewportRef.current;
      return !root || !startsOnHorizontalScroller(evt.target, root);
    },
  });

  // Only the visited panels and their immediate neighbours hold real content.
  // Mounting happens on settle, never mid-drag: Embla moves the track from the
  // main thread, so a React commit during the gesture is a visible stall.
  const [mounted, setMounted] = useState<Set<number>>(() => new Set([index]));

  const widen = useCallback((center: number) => {
    setMounted((prev) => {
      const next = new Set(prev);
      const n = panels.length;
      // Once a panel has been visited it stays mounted — remounting is
      // expensive and would throw away that panel's scroll position.
      [center, (center + 1) % n, (center - 1 + n) % n].forEach((i) => next.add(i));
      return next.size === prev.size ? prev : next;
    });
  }, [panels.length]);

  // Warm the rest of the panels one at a time in idle time, so a swipe never
  // lands on an unmounted panel and its queries have already resolved by the
  // time you get there. One per idle callback rather than all at once: mounting
  // six list pages in a single commit is a visible stall on a mid-range phone,
  // and this way the first panel's own render and fetches always go first.
  useEffect(() => {
    if (mounted.size >= panels.length) return;

    const warmNext = () => {
      setMounted((prev) => {
        if (prev.size >= panels.length) return prev;
        const next = new Set(prev);
        for (let i = 0; i < panels.length; i += 1) {
          if (!next.has(i)) {
            next.add(i);
            break;
          }
        }
        return next;
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(warmNext, { timeout: 2000 });
      return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(warmNext, 200);
    return () => window.clearTimeout(handle);
  }, [mounted, panels.length]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => onIndexChange(emblaApi.selectedScrollSnap());
    const onSettle = () => widen(emblaApi.selectedScrollSnap());

    emblaApi.on("select", onSelect);
    emblaApi.on("settle", onSettle);
    widen(emblaApi.selectedScrollSnap());

    if (import.meta.env.DEV && !emblaApi.internalEngine().slideLooper.canLoop()) {
      console.warn("SwipeTabs: loop disabled — panels do not fill the viewport");
    }

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("settle", onSettle);
    };
  }, [emblaApi, onIndexChange, widen]);

  // Each panel keeps its own scroll offset, so the chrome has to re-sync when
  // the active panel changes as well as while it scrolls.
  useEffect(() => {
    if (!onActiveScroll) return;
    const node = panelRefs.current[index];
    if (!node) return;

    // Synchronously, not through rAF: scroll events fire just before paint, so
    // deferring a frame leaves the chrome permanently one frame behind the
    // content, which reads as choppiness. The handler only writes two style
    // properties, so there's nothing here worth batching.
    const handler = () => onActiveScroll(node.scrollTop);

    onActiveScroll(node.scrollTop);
    node.addEventListener("scroll", handler, { passive: true });
    return () => node.removeEventListener("scroll", handler);
  }, [index, onActiveScroll, mounted]);

  // Tapping a pill drives the same carousel, so the two stay in sync.
  useEffect(() => {
    if (!emblaApi || emblaApi.selectedScrollSnap() === index) return;
    const jump = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    emblaApi.scrollTo(index, jump);
  }, [emblaApi, index]);

  return (
    <div
      className="h-full overflow-hidden"
      ref={(node) => {
        viewportRef.current = node;
        emblaRef(node);
      }}
    >
      <div className="flex h-full" style={{ touchAction: "pan-y pinch-zoom" }}>
        {panels.map((panel, i) => (
          <div
            key={panel.id}
            ref={(node) => {
              panelRefs.current[i] = node;
            }}
            role="tabpanel"
            id={`swipe-panel-${panel.id}`}
            aria-labelledby={panel.hasTab === false ? undefined : `swipe-tab-${panel.id}`}
            aria-label={panel.hasTab === false ? panel.label : undefined}
            className="min-w-0 shrink-0 grow-0 basis-full overflow-y-auto [overscroll-behavior-y:contain] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{
              height: panelHeight ?? "calc(100dvh - var(--swipe-chrome-h, 0px))",
              paddingBottom: panelPaddingBottom,
              // Names this scroller as the timeline the chrome's scroll-driven
              // animation follows. Declared in render rather than assigned from
              // an effect: the effect also keys on `mounted`, which now changes
              // repeatedly as panels warm up, and its cleanup could hand the name
              // back to a stale panel. Exactly one panel may carry the name — a
              // duplicate resolves to neither and the chrome silently stops
              // moving.
              scrollTimelineName: i === index ? "--stoop-active-panel" : undefined,
              scrollTimelineAxis: i === index ? "block" : undefined,
            } as CSSProperties}
          >
            {/* A spacer, not padding-top: a sticky child's constraint rectangle
                is the scroller's CONTENT box, so padding on the scroller would
                push every sticky filter row down by that much. */}
            {panelPaddingTop ? <div style={{ height: panelPaddingTop }} /> : null}
            {mounted.has(i) ? panel.render() : null}
          </div>
        ))}
      </div>
    </div>
  );
}
